import React, { useState } from 'react';
import { useGame } from '../contexts/GameContext';
import { t } from '../i18n/translations';
import { createLead } from '../services/api';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { ArrowLeft, Loader2 } from 'lucide-react';

const RegistrationForm = () => {
  const { language, settings, startGame, navigateTo } = useGame();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    azienda: '',
    email: '',
    telefono: '',
    citta: '',
    cap: ''
  });
  const [errors, setErrors] = useState({});
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const formTitle = language === 'it' 
    ? settings?.form_title_it || "Vuoi guidare la logistica del vino?"
    : settings?.form_title_en || "Want to drive wine logistics?";

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nome.trim()) newErrors.nome = t('errors.required', language);
    if (!formData.azienda.trim()) newErrors.azienda = t('errors.required', language);
    if (!formData.email.trim()) {
      // Email is optional — skip validation if empty
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('errors.invalidEmail', language);
    }
    if (!formData.telefono.trim()) {
      newErrors.telefono = t('errors.required', language);
    } else if (!/^[\d\s+()-]{6,}$/.test(formData.telefono)) {
      newErrors.telefono = t('errors.invalidPhone', language);
    }
    if (!formData.citta.trim()) newErrors.citta = t('errors.required', language);
    if (!formData.cap.trim()) newErrors.cap = t('errors.required', language);
    if (!privacyAccepted) newErrors.privacy = t('privacyRequired', language);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const lead = await createLead(formData);
      startGame(lead);
    } catch (error) {
      console.error('Error creating lead:', error);
      setErrors({ submit: 'Errore durante la registrazione. Riprova.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex flex-col" data-testid="registration-form">
      {/* Caution Stripe */}
      <div className="caution-stripe" />
      
      {/* Header */}
      <div className="p-4 flex items-center">
        <button
          onClick={() => navigateTo('attract')}
          className="p-2 text-[#94a3b8] hover:text-white transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
      </div>

      {/* Form Container */}
      <div className="flex-1 flex flex-col items-center px-4 pb-8 overflow-y-auto">
        {/* Title */}
        <div className="text-center mb-6">
          <h1 className="font-arcade text-lg sm:text-xl text-[#FFD100] mb-2">
            {formTitle}
          </h1>
          <p className="text-[#94a3b8] text-sm">
            {t('formSubtitle', language)}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="w-full max-w-md space-y-4">
          {/* Nome */}
          <div>
            <label className="block text-[#94a3b8] text-sm mb-1">{t('nome', language)} *</label>
            <Input
              type="text"
              value={formData.nome}
              onChange={(e) => handleChange('nome', e.target.value)}
              className={`arcade-input ${errors.nome ? 'border-red-500' : ''}`}
              placeholder={t('nome', language)}
              data-testid="input-nome"
            />
            {errors.nome && <p className="text-red-500 text-xs mt-1">{errors.nome}</p>}
          </div>

          {/* Azienda */}
          <div>
            <label className="block text-[#94a3b8] text-sm mb-1">{t('azienda', language)} *</label>
            <Input
              type="text"
              value={formData.azienda}
              onChange={(e) => handleChange('azienda', e.target.value)}
              className={`arcade-input ${errors.azienda ? 'border-red-500' : ''}`}
              placeholder={t('azienda', language)}
              data-testid="input-azienda"
            />
            {errors.azienda && <p className="text-red-500 text-xs mt-1">{errors.azienda}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-[#94a3b8] text-sm mb-1">{t('email', language)}</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className={`arcade-input ${errors.email ? 'border-red-500' : ''}`}
              placeholder="email@example.com"
              data-testid="input-email"
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          {/* Telefono */}
          <div>
            <label className="block text-[#94a3b8] text-sm mb-1">{t('telefono', language)} *</label>
            <Input
              type="tel"
              value={formData.telefono}
              onChange={(e) => handleChange('telefono', e.target.value)}
              className={`arcade-input ${errors.telefono ? 'border-red-500' : ''}`}
              placeholder="+39 123 456 7890"
              data-testid="input-telefono"
            />
            {errors.telefono && <p className="text-red-500 text-xs mt-1">{errors.telefono}</p>}
          </div>

          {/* Città & CAP */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[#94a3b8] text-sm mb-1">{t('citta', language)} *</label>
              <Input
                type="text"
                value={formData.citta}
                onChange={(e) => handleChange('citta', e.target.value)}
                className={`arcade-input ${errors.citta ? 'border-red-500' : ''}`}
                placeholder={t('citta', language)}
                data-testid="input-citta"
              />
              {errors.citta && <p className="text-red-500 text-xs mt-1">{errors.citta}</p>}
            </div>
            <div>
              <label className="block text-[#94a3b8] text-sm mb-1">{t('cap', language)} *</label>
              <Input
                type="text"
                value={formData.cap}
                onChange={(e) => handleChange('cap', e.target.value)}
                className={`arcade-input ${errors.cap ? 'border-red-500' : ''}`}
                placeholder="00000"
                data-testid="input-cap"
              />
              {errors.cap && <p className="text-red-500 text-xs mt-1">{errors.cap}</p>}
            </div>
          </div>

          {/* Privacy Checkbox */}
          <div className="space-y-2" data-testid="privacy-section">
            <div className="flex items-start gap-3">
              <Checkbox
                id="privacy"
                checked={privacyAccepted}
                onCheckedChange={(checked) => {
                  setPrivacyAccepted(!!checked);
                  if (errors.privacy) setErrors(prev => ({ ...prev, privacy: undefined }));
                }}
                className="mt-1 border-[#475569] data-[state=checked]:bg-[#E31837] data-[state=checked]:border-[#E31837]"
                data-testid="privacy-checkbox"
              />
              <label htmlFor="privacy" className="text-[#94a3b8] text-xs leading-relaxed cursor-pointer select-none">
                {language === 'it'
                  ? (settings?.privacy_text_it || "Acconsento al trattamento dei miei dati personali ai sensi del Regolamento UE 2016/679 (GDPR).")
                  : (settings?.privacy_text_en || "I consent to the processing of my personal data in accordance with EU Regulation 2016/679 (GDPR).")
                }
                {settings?.privacy_link_it && language === 'it' && (
                  <>
                    {' '}
                    <a
                      href={settings.privacy_link_it}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-[#3B82F6] underline hover:text-[#60A5FA]"
                      data-testid="privacy-link"
                    >
                      {settings?.privacy_link_label_it || "Leggi l'informativa completa"}
                    </a>
                  </>
                )}
                {settings?.privacy_link_en && language === 'en' && (
                  <>
                    {' '}
                    <a
                      href={settings.privacy_link_en}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-[#3B82F6] underline hover:text-[#60A5FA]"
                      data-testid="privacy-link"
                    >
                      {settings?.privacy_link_label_en || "Read the full privacy policy"}
                    </a>
                  </>
                )}
              </label>
            </div>
            {errors.privacy && <p className="text-red-500 text-xs ml-7" data-testid="privacy-error">{errors.privacy}</p>}
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <p className="text-red-500 text-sm text-center">{errors.submit}</p>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-14 bg-[#E31837] hover:bg-[#c41530] text-white font-arcade text-xs arcade-btn pixel-shadow"
            data-testid="submit-button"
          >
            {isSubmitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              t('submit', language)
            )}
          </Button>

          {/* Skip Registration Button (for testing) */}
          {settings?.show_skip_registration && (
            <button
              type="button"
              onClick={() => startGame({ id: 'test-user', nome: 'Test', azienda: 'Test', email: 'test@test.com', telefono: '0000000000', citta: 'Test', cap: '00000' })}
              className="w-full mt-4 py-3 text-[#64748b] hover:text-[#94a3b8] text-sm underline transition-colors"
              data-testid="skip-registration-btn"
            >
              {language === 'it' ? 'Salta registrazione (test)' : 'Skip registration (test)'}
            </button>
          )}
        </form>
      </div>

      {/* Bottom Caution Stripe */}
      <div className="caution-stripe" />
    </div>
  );
};

export default RegistrationForm;
